import { OxySelector } from "@/oxygen/classes/lib/types";
import { pipe } from "fp-ts/function";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { fold } from "fp-ts/Either";
import defensiveCopy from "@/util/defensive-copy";
import { configStore, globalStore } from "@/store/store-accessor";
import { parseVariablesInSelector } from "@/oxygen/variables/lib/utils";
import { generateCss } from "@/lib/canvas/styles";
import { addOrUpdateGeneratedStylesInCanvasHead } from "@/lib/canvas/assets/css-in-head/global-styles";
import murmurhash from "murmurhash";
import { replaceAmpersandInSelector } from "@/oxygen/classes/lib/utils";

const cacheStore = new Map<string, { hash: number; css: string }>();

export function handleOxySelectorsChanged(canvasDocument: Document) {
  const selectors = getParsedSelectors();

  const css = selectors.map(getCssForSelector).join(" ");
  addOrUpdateGeneratedStylesInCanvasHead(canvasDocument, "oxy-selectors", css);
}

function getParsedSelectors(): OxySelector[] {
  return defensiveCopy(globalStore.oxySelectors).map(selector => {
    return parseVariablesInSelector<OxySelector>(
      replaceAmpersandInSelector(selector)
    );
  });
}

function getCssForSelector(selector: OxySelector): string {
  const cachedData = cacheStore.get(selector.id);

  if (cachedData && !hasSelectorChanged(selector, cachedData.hash)) {
    return cachedData.css;
  }

  const generatedCss = generateCss(
    {
      selector
    },
    configStore.breakpoints,
    configStore.oxySelectorsControlsAndTemplate.template,
    {},
    configStore.oxySelectorsControlsAndTemplate
      .propertyPathsToWhitelistInFlatProps
  );

  cacheStore.set(selector.id, {
    hash: generateHash(selector),
    css: generatedCss
  });

  return generatedCss;
}

function hasSelectorChanged(
  selector: OxySelector,
  cachedHash: number
): boolean {
  return cachedHash !== generateHash(selector);
}

function generateHash(selector: OxySelector) {
  const jsonString = pipe(
    fpJsonStringify(selector),
    fold(
      () => "",
      result => result
    )
  );

  return murmurhash(jsonString);
}
